import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, ResolveEnd, Router } from '@angular/router';
import { ApplicationInsights, ITelemetryItem, SeverityLevel } from '@microsoft/applicationinsights-web';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { APP_CONFIG } from '../shared-services/services/config.service';
import { SessionStorageService } from '../shared-services/services/session-storage.service';

interface CookieCensus {
    count: number;
    bytes: number;
    names: string;
}

@Injectable({
    providedIn: 'root'
})
export class MonitoringService {

    private routerSubscription: Subscription;

    private appInsights: ApplicationInsights;

    private cookieCensus: CookieCensus;

    constructor(
        private router: Router,
        private sessionStorageService: SessionStorageService
    ) {
        if (APP_CONFIG.appInsightsConfig) {
            this.appInsights = new ApplicationInsights({
                config: {
                    connectionString: APP_CONFIG.appInsightsConfig.connectionString
                }
            });

            this.appInsights.loadAppInsights();

            this.appInsights.addTelemetryInitializer((envelope: ITelemetryItem) => {
                envelope.data = { ...envelope.data, ...this.getGlobalProperties() };

                return true;
            });
        }
        // Application Insights
        this.routerSubscription = this.router.events
            .pipe(filter(event => event instanceof ResolveEnd))
            .subscribe((event: ResolveEnd) => {
                const activatedComponent = this.getActivatedComponent(event.state.root);
                if (activatedComponent) {
                    this.logPageView(`${activatedComponent.name}_${this.getRouteTemplate(event.state.root)}`, event.urlAfterRedirects);
                }

                let url = event.urlAfterRedirects;

                if (url.includes('returnUrl')) {
                    url = decodeURIComponent(url.split('returnUrl=')[1]);
                }

                this.sessionStorageService.saveLastVisitedUrl(url);
            });
    }

    private getActivatedComponent(snapshot: ActivatedRouteSnapshot): any {

        if (snapshot.firstChild) {
            return this.getActivatedComponent(snapshot.firstChild);
        }

        return snapshot.component;
    }

    private getRouteTemplate(snapshot: ActivatedRouteSnapshot): string {
        let path = '';
        if (snapshot.routeConfig) {
            path += snapshot.routeConfig.path;
        }

        if (snapshot.firstChild) {
            return path + this.getRouteTemplate(snapshot.firstChild);
        }

        return path;
    }

    // Names only. Cookie values are session credentials and must never be logged.
    private getCookieCensus(): CookieCensus {
        try {
            const cookie = document.cookie || '';

            if (!this.cookieCensus || this.cookieCensus.bytes !== cookie.length) {
                const names = cookie.split(';')
                    .map(pair => pair.split('=')[0].trim())
                    .filter(name => !!name);

                this.cookieCensus = {
                    count: names.length,
                    bytes: cookie.length,
                    names: names.join(',')
                };
            }

            return this.cookieCensus;
        } catch {
            return undefined;
        }
    }

    private getGlobalProperties(): { [key: string]: string | number } {
        const properties: { [key: string]: string | number } = {
            host: window.location.host
        };

        try {
            const companyCode = this.sessionStorageService.getItem('companyCode');

            if (companyCode) {
                properties['companyCode'] = companyCode;
            }
        } catch {
        }

        try {
            const authToken = this.sessionStorageService.getAuthToken();

            if (authToken) {
                properties['flId'] = authToken.flId;
                properties['flCompany'] = authToken.flAuthGroup;
                properties['flSessionId'] = authToken.flSessionId;
            }
        } catch {
        }

        const census = this.getCookieCensus();

        if (census) {
            properties['cookieCount'] = census.count;
            properties['cookieBytes'] = census.bytes;
            properties['cookieNames'] = census.names;
        }

        return properties;
    }

    public logPageView(name: string, url?: string, properties?: { [key: string]: string }) {
        if (this.appInsights) {
            this.appInsights.trackPageView({
                name: name,
                uri: url,
                properties: properties
            });
        }
    }

    public logEvent(name: string, properties?: { [key: string]: string }, measurements?: { [key: string]: number }) {
        if (this.appInsights) {
            this.appInsights.trackEvent({
                name: name,
                properties: properties
            });
        }
    }

    public logError(error: Error, properties?: { [key: string]: string }, measurements?: { [key: string]: number }) {
        if (this.appInsights) {
            this.appInsights.trackException({
                exception: error,
                severityLevel: SeverityLevel.Critical,
                properties: properties
            });
        }
    }
}
